import { useForm } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import {
  createFileRoute,
  Link,
  Navigate,
  useNavigate,
} from "@tanstack/react-router";
import { verifyPasswordResetCode, confirmPasswordReset } from "firebase/auth";
import GoogleLogo from "@/assets/icons/google-logo.svg";
import MicrosoftLogo from "@/assets/icons/microsoft-logo.svg";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { auth } from "@/auth/firebase";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import ErrorText from "@/components/universal/error-text";
import { useEffect, type JSX } from "react";
import {
  handleCredentialSignIn,
  handleGoogleSignIn,
  handleMicrosoftSignIn,
  usePendingCredential,
  useSsoSignIn,
} from "./-auth-functions";
import { FirebaseError } from "firebase/app";
import { useQueryState } from "nuqs";
import VerifyEmailAction from "./-verify-email-action";

export const Route = createFileRoute("/auth/action")({
  component: AccountActionPage,
});

interface CreateAccountForm {
  email: string;
  password: string;
}

const resetPasswordErrorMessages: Record<string, string> = {
  "auth/weak-password":
    "Password is too weak. Please choose a stronger password.",
  "auth/expired-action-code":
    "This link has expired. Please request a new password reset link.",
  "auth/invalid-action-code":
    "This link is invalid. Please log in with SSO or request a new password reset link.",
};

/**
 * AccountActionPage - Handles multiple firebase actions
 * 1. Password reset
 * 2. Email verification
 * 3. Email change
 *
 * For our purposes, this is also where the user will set their password
 * for the account that was created for them.
 *
 * `resetPassword` is handled inline (set-password form); `verifyEmail` is
 * delegated to {@link VerifyEmailAction}. Email change is not supported.
 *
 * See example: https://firebase.google.com/docs/auth/custom-email-handler
 * @returns {JSX.Element} The AccountActionPage component
 */
function AccountActionPage(): JSX.Element {
  const [mode] = useQueryState("mode");
  const [oobCode] = useQueryState("oobCode");
  const form = useForm<CreateAccountForm>({
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const {
    set: setPendingCredential,
    get: getPendingCredential,
    clear: clearPendingCredential,
  } = usePendingCredential();

  const { ssoPending, runSsoSignIn } = useSsoSignIn();

  const navigate = useNavigate();
  const onMfaRequired = () => navigate({ to: "/auth/mfa-challenge" });

  const { mutate: setPasswordAndSignIn, isPending } = useMutation({
    mutationFn: async (formData: CreateAccountForm) => {
      if (!oobCode) {
        throw new FirebaseError("missing_oob_code", "Missing action code");
      }
      await confirmPasswordReset(auth, oobCode, formData.password);
    },
    onSuccess: (_, formData) =>
      handleCredentialSignIn({
        form,
        email: formData.email,
        password: formData.password,
        getPendingCredential,
        clearPendingCredential,
        onMfaRequired,
      }),
    onError: (error) => {
      const code = (error as FirebaseError).code;
      form.setError("root.serverError", {
        type: code,
        message:
          resetPasswordErrorMessages[code] ??
          "Something went wrong setting your password. Please contact support.",
      });
    },
  });

  // Verify the password reset code is valid.
  useEffect(() => {
    async function verifyPasswordResetCodeAndSetEmail() {
      if (!mode || !oobCode || mode !== "resetPassword") {
        return;
      }
      try {
        const accountEmail = await verifyPasswordResetCode(auth, oobCode);
        form.setValue("email", accountEmail);
      } catch (error) {
        form.setError("root.serverError", {
          type: (error as FirebaseError).code,
          message:
            "Link is invalid or expired. Please log in with SSO or request a new password reset link.",
        });
      }
    }

    verifyPasswordResetCodeAndSetEmail();
  }, [form, mode, oobCode]);

  if (mode === "verifyEmail" && oobCode) {
    return <VerifyEmailAction oobCode={oobCode} />;
  }

  // If any required query parameter is missing, redirect to the login page
  if (!mode || !oobCode || mode !== "resetPassword") {
    return <Navigate to="/auth/login" replace />;
  }

  return (
    <div className="flex w-full max-w-sm flex-col gap-6">
      <title>Set Password - Legrande Health Portal</title>
      <Card className="sm:max-w-96 w-full text-center">
        <CardHeader>
          <CardTitle>Sign In</CardTitle>
          <CardDescription>
            Use SSO or set your new password below
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center gap-4">
            <Button
              onClick={() =>
                runSsoSignIn(() =>
                  handleGoogleSignIn({
                    form,
                    getPendingCredential,
                    clearPendingCredential,
                    onMfaRequired,
                  }),
                )
              }
              disabled={ssoPending}
              variant={"outline"}
              className="w-full text-md font-normal"
            >
              <img
                className="size-6 mr-2"
                src={GoogleLogo}
                loading="lazy"
                alt="google logo"
              />
              Sign in with Google
            </Button>
            <Button
              onClick={() =>
                runSsoSignIn(() =>
                  handleMicrosoftSignIn({
                    form,
                    setPendingCredential,
                    onMfaRequired,
                  }),
                )
              }
              disabled={ssoPending}
              variant={"outline"}
              className="w-full text-md font-normal"
            >
              <img
                className="size-6 mr-2"
                src={MicrosoftLogo}
                loading="lazy"
                alt="microsoft logo"
              />
              Sign in with Microsoft
            </Button>
          </div>
          <div className="flex items-center justify-center gap-4 mt-4">
            <Separator className="my-4 flex-1" />
            <span className="text-muted-foreground text-sm">or</span>
            <Separator className="my-4 flex-1" />
          </div>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit((formData) =>
                setPasswordAndSignIn(formData),
              )}
              className="text-left space-y-4"
            >
              <FormField
                control={form.control}
                rules={{ required: "Email is required" }}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="text"
                        autoComplete="email"
                        disabled={true}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                rules={{
                  required: "Password is required",
                  validate: {
                    hasUpperCase: (value) =>
                      /[A-Z]/.test(value) ||
                      "Must include at least one uppercase letter",
                    hasLowerCase: (value) =>
                      /[a-z]/.test(value) ||
                      "Must include at least one lowercase letter",
                    hasNumber: (value) =>
                      /\d/.test(value) || "Must include at least one number",
                    hasSpecialChar: (value) =>
                      /[!@#$%^&*(),.?":{}|<>]/.test(value) ||
                      "Must include at least one special character",
                    minLength: (value) =>
                      value.length >= 8 || "Must be at least 8 characters long",
                  },
                }}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>New Password</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="password"
                        autoComplete="current-password"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <p className="text-sm text-muted-foreground">
                Password must be at least 8 characters long, contain at least 1
                uppercase letter, 1 lowercase letter, 1 number, and 1 special
                character.
              </p>

              <div className="pt-2">
                <Button type="submit" disabled={isPending} className="w-full">
                  Set Password
                </Button>
              </div>

              {form.formState.errors.root?.serverError && (
                <ErrorText
                  text={
                    form.formState.errors.root?.serverError.message ||
                    "Error setting password. Please contact support."
                  }
                />
              )}
            </form>
          </Form>
        </CardContent>
      </Card>
      <div className="text-muted-foreground *:[a]:hover:text-primary text-center text-xs text-balance *:[a]:underline *:[a]:underline-offset-4">
        By clicking sign in, you agree to our{" "}
        <Link to="/terms-of-use">Terms of Use</Link> and{" "}
        <Link to="/privacy-policy">Privacy Policy</Link>
      </div>
    </div>
  );
}
